import React, { useEffect, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { PaymentType, usePaymentTypeStore } from '../../store/usePaymentTypeStore';
import { Button, Label, Modal, Popover, TextInput, Select, ToggleSwitch } from 'flowbite-react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

export default function PaymentTypesPage() {
  const getPaymentTypes = usePaymentTypeStore((state) => state.getPaymentTypes);
  const deletePaymentType = usePaymentTypeStore((state) => state.deletePaymentType);
  const paymentTypes = usePaymentTypeStore((state) => state.paymentTypes);
  const createPaymentType = usePaymentTypeStore((state) => state.createPaymentType);
  const updatePaymentType = usePaymentTypeStore((state) => state.updatePaymentType);
  const loading = usePaymentTypeStore((state) => state.loading);
  const pagination = usePaymentTypeStore((state) => state.pagination);

  const canAccess = useCanAccessResource('payment-types');
  const hasReadPermission = useHasPermission('payment-types', 'read');
  const hasCreatePermission = useHasPermission('payment-types', 'create');
  const hasUpdatePermission = useHasPermission('payment-types', 'update');
  const hasDeletePermission = useHasPermission('payment-types', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [tableData, setTableData] = useState<PaymentType[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  const [name, setName] = useState('');
  const [category, setCategory] = useState<'subscription' | 'event' | 'donation'>('subscription');
  const [frequency, setFrequency] = useState<'monthly' | 'yearly' | 'oneTime'>('yearly');
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<string>('INR');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, isActiveFilter]);

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getPaymentTypes(search || undefined, currentPage, limit, isActiveFilter);
    }
  }, [getPaymentTypes, search, currentPage, limit, isActiveFilter, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    setTableData(paymentTypes);
  }, [paymentTypes]);

  const handleAddOrEdit = async () => {
    if (!name || !amount || amount <= 0) {
      toast.error('Please fill in all required fields with valid values.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    if (modalType === 'add') {
      await createPaymentType({
        name,
        category,
        frequency,
        amount,
        currency,
        startDate: startDate || null,
        endDate: endDate || null,
      });
    } else {
      if (!editId) return;
      await updatePaymentType(editId, {
        name,
        category,
        frequency,
        isActive,
        startDate: startDate || null,
        endDate: endDate || null,
      });
    }

    setOpenAddModal(false);
    setName('');
    setCategory('subscription');
    setFrequency('yearly');
    setAmount(0);
    setCurrency('INR');
    setStartDate('');
    setEndDate('');
    setIsActive(true);
    setEditId(null);
    setModalType('add');
  };

  const handleEdit = (paymentType: PaymentType) => {
    setEditId(paymentType._id);
    setModalType('edit');
    setOpenAddModal(true);
    setName(paymentType.name);
    setCategory(paymentType.category);
    setFrequency(paymentType.frequency);
    setAmount(paymentType.amount);
    setCurrency(paymentType.currency);
    setIsActive(paymentType.isActive);
    setStartDate(paymentType.startDate ? dayjs(paymentType.startDate).format('YYYY-MM-DD') : '');
    setEndDate(paymentType.endDate ? dayjs(paymentType.endDate).format('YYYY-MM-DD') : '');
  };

  const handleDelete = () => {
    if (selectedPaymentType !== null) {
      deletePaymentType(selectedPaymentType);
      setOpenModal(false);
      setSelectedPaymentType(null);
    }
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Payment Types" />;
  }

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedPaymentType(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this payment type?
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {"Yes, I'm sure"}
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedPaymentType(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="lg"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setName('');
          setCategory('subscription');
          setFrequency('yearly');
          setAmount(0);
          setCurrency('INR');
          setStartDate('');
          setEndDate('');
          setIsActive(true);
          setEditId(null);
          setModalType('add');
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add Payment Type' : 'Edit Payment Type'}
            </h3>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="name"
                type="text"
                placeholder="e.g., Yearly Subscription 2000"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="category" value="Category *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value as 'subscription' | 'event' | 'donation')}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
              >
                <option value="subscription">Subscription</option>
                <option value="event">Event</option>
                <option value="donation">Donation</option>
              </Select>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="frequency" value="Frequency *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as 'monthly' | 'yearly' | 'oneTime')}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="oneTime">One Time</option>
              </Select>
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="amount" value="Amount *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="amount"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
                helperText={modalType === 'edit' ? 'Amount cannot be modified after creation' : ''}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="currency" value="Currency *" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <Select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={modalType === 'edit'}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
                <option value="JPY">JPY</option>
                <option value="CNY">CNY</option>
                <option value="SGD">SGD</option>
                <option value="AED">AED</option>
              </Select>
              {modalType === 'edit' && (
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Currency cannot be modified after creation
                </p>
              )}
            </div>

            {modalType === 'edit' && (
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="isActive" value="Active Status" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <ToggleSwitch checked={isActive} onChange={setIsActive} label={isActive ? 'Active' : 'Inactive'} />
              </div>
            )}

            <div>
              <div className="mb-2 block">
                <Label htmlFor="startDate" value="Start Date (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <div className="mb-2 block">
                <Label htmlFor="endDate" value="End Date (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
              </div>
              <TextInput
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div className="w-full">
              <Button
                onClick={handleAddOrEdit}
                disabled={loading || !name || !amount || amount <= 0}
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : modalType === 'add' ? 'Add Payment Type' : 'Edit Payment Type'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="Payment Types" description="Manage payment types" />
      <PageBreadcrumb pageTitle="Payment Types" />
      <div className="space-y-6">
        <ComponentCard title="Payment Types">
          <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5">
            {(hasCreatePermission || isMainAdmin) && (
              <Button
                onClick={() => {
                  setOpenAddModal(true);
                  setModalType('add');
                  setName('');
                  setCategory('subscription');
                  setFrequency('yearly');
                  setAmount(0);
                  setCurrency('INR');
                  setStartDate('');
                  setEndDate('');
                  setIsActive(true);
                }}
                className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                Add Payment Type
              </Button>
            )}
            <div className="relative">
              <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                <svg
                  className="fill-gray-500 dark:fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                    fill=""
                  />
                </svg>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search payment types..."
                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
            <Select
              value={isActiveFilter === undefined ? 'all' : isActiveFilter ? 'active' : 'inactive'}
              onChange={(e) => {
                const value = e.target.value;
                setIsActiveFilter(value === 'all' ? undefined : value === 'active');
              }}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </Select>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1200px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Frequency
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Total Collected
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Payment Count
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No payment types found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((paymentType) => (
                        <TableRow key={paymentType._id}>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {paymentType.name}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                paymentType.category === 'subscription'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : paymentType.category === 'event'
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              }`}
                            >
                              {paymentType.category.charAt(0).toUpperCase() + paymentType.category.slice(1)}
                            </span>
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {paymentType.frequency.charAt(0).toUpperCase() + paymentType.frequency.slice(1)}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {paymentType.currency} {paymentType.amount.toFixed(2)}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {paymentType.currency} {(paymentType.totalCollected || 0).toFixed(2)}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {paymentType.paymentCount || 0}
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                paymentType.isActive
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                              }`}
                            >
                              {paymentType.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell
                            className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400"
                            style={{ fontFamily: 'Lato, sans-serif' }}
                          >
                            {dayjs(paymentType.createdAt).format('DD MMM YYYY')}
                          </TableCell>
                          <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <Popover
                                placement="top"
                                aria-labelledby="default-popover"
                                content={
                                  <div className="py-2 px-3">
                                    {(hasDeletePermission || isMainAdmin) && (
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => {
                                          setSelectedPaymentType(paymentType._id);
                                          setOpenModal(true);
                                        }}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-trash-2"
                                        >
                                          <path d="M3 6h18" />
                                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                          <line x1="10" x2="10" y1="11" y2="17" />
                                          <line x1="14" x2="14" y1="11" y2="17" />
                                        </svg>
                                        Delete
                                      </div>
                                    )}

                                    {(hasUpdatePermission || isMainAdmin) && (
                                      <div
                                        className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                        onClick={() => handleEdit(paymentType)}
                                        style={{ fontFamily: 'Lato, sans-serif' }}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-pencil"
                                        >
                                          <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                          <path d="m15 5 4 4" />
                                        </svg>
                                        Edit
                                      </div>
                                    )}
                                  </div>
                                }
                              >
                                <button
                                  className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                  style={{ fontFamily: 'Lato, sans-serif' }}
                                >
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                      fill="currentColor"
                                    />
                                  </svg>
                                </button>
                              </Popover>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
